import ArkLoader from '@src/js/common/ark-extensions';
import { IMobileScreenshotExtension } from '@src/js/common/ark-extensions/dc-components/mobile-screenshot/interface';
import componentConfig from '@src/js/common/ark-extensions/dc-components/mobile-screenshot/component-config';
import { PadType } from '@tencent/docs-mime-types';
import {
    IMobileScreenshotService,
    IMobileScreenshotServiceDelegates,
    IMobileScreenshotServiceInjectable,
} from './interface';
import { IBaseCopyPaste } from '../../copy-paste/core/base/base-copy-paste.interface';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import html2canvas from 'html2canvas';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import Snackbar from '@tencent/dui-mobile/lib/components/Snackbar';

export class MobileScreenshotService implements IMobileScreenshotService {
    public copyPasteManager?: IBaseCopyPaste;
    private services: IMobileScreenshotServiceDelegates;

    public constructor(private injectable?: IMobileScreenshotServiceInjectable) {
    }

    public setServiceDelegates(services: Partial<IMobileScreenshotServiceDelegates>): void {
        this.services = {
            ...this.services,
            ...services,
        };
    }

    public init = async () => {
        if (!browser.isMobile) return;
        // 确保dc组件文件下载完成
        await ArkLoader.initPromise;
        const extension = (await ArkLoader.extensionService
            .getExtension<IMobileScreenshotExtension>(componentConfig.name)) as IMobileScreenshotExtension;

        const getExportImageOptions = () =>
            this.services.exportImageManager.getExportImageMobileOptions({
                position: 'prtscnbar',
                isMobile: true,
                onFetchPage: () => {
                    this.services.exportImageManager.isImageCopying = true;
                },
                onClose: () => {
                    this.services.exportImageManager.isImageCopying = false;
                },
            });
        // 组件初始化
        extension.show({
            padType: PadType.Slide,
            getExportImageOptions,
            /**
             * 判断是否在正文页面,非正文状态下需要禁用截图功能；true - 正文，false - 非正文
             */
            isElementOnTop: this.withoutElementOnPage,
            hasAuthority: () => !!SlideDataServices.PrivilegeManager()?.printable,
            getScreenshotData: this.getScreenshotData,
            beforeScreenshot: this.beforeScreenshot,
            onConvertToPDF: this.services.pdfTransferService.transferToPDFMobile,
        });
    };

    /**
     * 截图前的检查 是否允许截图
     */
    private beforeScreenshot = async () => !this.services.entryStateService.checkIfEditMode?.();

    // 是否正文: 没有其他弹窗或者抽屉打开
    private withoutElementOnPage = () => {
        const centerElement = document.elementFromPoint(document.body.clientWidth / 2, document.body.clientHeight / 2);
        const showNoteArea = this.services.noteAreaManager.listShow;
        const hasSnackbar = Snackbar.hasVisibleInstances();
        const isOnSlidePage = /\/slide\//.test(location.pathname);
        const { fullscreenElement } = document;
        // ios下全屏播放视频场景 fullscreenElement拿不到
        const { isVideoFullScreen } = this.services.mediaManager;
        return !!centerElement?.closest('#workbench-content-container') && !showNoteArea && !hasSnackbar && isOnSlidePage && !fullscreenElement && !isVideoFullScreen;
    };

    private getScreenshotData: () => Promise<string> = () =>
        new Promise((resolve, reject) => {
            const { isPlaying } = this.services.playManager;
            let targetDiv;
            let offsetY = 0;
            if (isPlaying) {
                targetDiv = document.getElementById('grid-root');
            } else {
                targetDiv = document.getElementById('slide-list-m-container');
                const { scrollTop } = targetDiv;
                const marginTop = parseFloat(targetDiv.children[0].children[0].style.marginTop);
                offsetY = Math.max(marginTop - scrollTop, 0);
            }
            if (!targetDiv) {
                reject(new Error('Target element not found'));
                return;
            }

            html2canvas(targetDiv, {
                width: window.innerWidth,
                height: window.innerHeight - offsetY,
                y: offsetY,
                backgroundColor: '#D6D6D6',
            })
                .then((canvas) => {
                    const imgData = canvas.toDataURL('image/png');
                    resolve(imgData); // 解析 Promise 并返回图片数据
                })
                .catch((error) => {
                    reject(new Error('Screenshot failed'));
                });
        });
}
